import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useRemoveDocumentFromPackageMutation } from "#graphql/supergraphHooks";
import { hasConflictCode, REMOVE_DOCUMENT_CONFLICT_CODES } from "../utils/graphqlConflictCodes";
import { useCoverageRefresh } from "./useCoverageRefresh";
import { useRequiredArenaId } from "./useRequiredArenaId";

/** Outcome of a removal attempt: `removed` (close the modal), `conflict` (the
 *  row went stale underneath the user — close the modal, data is refetching),
 *  or `error` (keep the modal open so the user can retry). */
export type RemoveDocumentOutcome = "removed" | "conflict" | "error";

export type UseRemoveDocument = {
  removeDocument: (packageDocumentId: string) => Promise<RemoveDocumentOutcome>;
  /** True while the removal write is in flight (drives the modal spinner). */
  isRemoving: boolean;
};

/**
 * Wraps `removeDocumentFromPackage`. Removal is a terminal supersession
 * server-side (never-delete); the payload returns the post-removal package in
 * the PackageDetail shape, so Apollo normalizes it into the cache by id and the
 * slideout's document list and header stats update without a refetch. The
 * coverage grid is a separate query — nudge it through the cross-subtree refresh
 * seam, since the removed document can flip the cell's state (e.g. to
 * EMPTY_PACKAGE when the package empties).
 *
 * Typed conflicts (BINDING_REPLACED — the binding was superseded by a
 * re-upload; NOT_FOUND — gone in another tab) are refetch signals, not error
 * dead-ends: neutral toast + resync. A repeated removal of an
 * already-removed binding returns success server-side (safe retry), so it
 * lands on the success path naturally.
 *
 * @param arenaId Write-path arena id. Writes fire on user action, well after the
 * host app's arena resolves, so an absent id here is a programming error that
 * throws rather than silently no-ops.
 */
export function useRemoveDocument(
  refetch: () => void,
  packageId: string | null,
  arenaId: number | undefined,
): UseRemoveDocument {
  const getArenaId = useRequiredArenaId(arenaId);
  const refreshCoverage = useCoverageRefresh();
  const [mutate] = useRemoveDocumentFromPackageMutation();
  const [isRemoving, setIsRemoving] = useState(false);
  // Synchronous re-entrancy guard: `isRemoving` state lags a render behind, so
  // two rapid confirms could both pass the state check and race on the shared
  // flag. A ref flips immediately, so the second call is dropped before it
  // fires a duplicate mutation.
  const inFlightRef = useRef(false);

  const removeDocument = useCallback(
    async (packageDocumentId: string): Promise<RemoveDocumentOutcome> => {
      if (inFlightRef.current) {
        return "error";
      }
      inFlightRef.current = true;
      setIsRemoving(true);
      try {
        // Write-path seam: a missing arena or package here is a programming
        // error. The throws land in the catch below, so the user still gets
        // the error toast instead of a silent no-op with the modal stuck open.
        const resolvedArenaId = getArenaId();
        if (packageId === null) {
          throw new Error("Package is not available");
        }
        await mutate({
          variables: {
            input: { arenaId: resolvedArenaId, packageId, packageDocumentId },
          },
        });
        toast.success("Document removed from package.");
        refreshCoverage();
        return "removed";
      } catch (error) {
        if (hasConflictCode(error, REMOVE_DOCUMENT_CONFLICT_CODES)) {
          toast.info("This document changed since it loaded — refreshing.");
          // The binding moved in another tab/session, so both surfaces are
          // stale: resync the slideout AND the coverage grid.
          refetch();
          refreshCoverage();
          return "conflict";
        }
        toast.error("Couldn’t remove this document. Please try again.");
        refetch();
        return "error";
      } finally {
        setIsRemoving(false);
        inFlightRef.current = false;
      }
    },
    [getArenaId, mutate, packageId, refetch, refreshCoverage],
  );

  return useMemo(() => ({ removeDocument, isRemoving }), [removeDocument, isRemoving]);
}
